// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import { query, where, collection, onSnapshot } from 'firebase/firestore';

import { getColorPalette } from "../../utils/colorPaletteUtils";

// React hook to get supplier timeframes
export function useSupplierTimeframes(firestore, suppliers, clientId, deviceId, timeframe, showSuppliers = true) {
    const [supplierTimeframes, setSupplierTimeframes] = React.useState([]);
    const unsubscribe = React.useRef(null);
    React.useEffect(() => {
        if (!showSuppliers) {
            unsubscribe.current?.();
            setSupplierTimeframes([]);
            return;
        }

        const q = query(
            collection(firestore, "clients", clientId, "devices", deviceId, "bale_feed_timestamps"),
            where("start_timestamp", ">=", timeframe.startDate),
            where("start_timestamp", "<", timeframe.endDate),
        );
        unsubscribe.current = onSnapshot(q, (snapshot) => {
            const supplierTimeframes = snapshot.docs.map((doc) => {
                const data = doc.data();
                if (!(data.supplier_id in suppliers)) {
                    return null;
                }
                return {
                    supplier: suppliers[data.supplier_id].display_name,
                    start_timestamp: data.start_timestamp?.toDate(),
                    end_timestamp: data.end_timestamp?.toDate()
                };
            });
            setSupplierTimeframes(supplierTimeframes);
        });
        return unsubscribe.current;
    }, [timeframe, showSuppliers]);
    return supplierTimeframes;
};

// Custom plugin to draw supplier background sections
export const supplierBackgroundPlugin = {
    id: 'supplierBackground',
    beforeDraw: (chart, args, options) => {
        const { ctx, chartArea, scales } = chart;
        const { supplierTimeframes } = options;
        
        if (!supplierTimeframes || supplierTimeframes.length === 0) return;
        
        // Create supplier color mapping
        const uniqueSuppliers = [...new Set(supplierTimeframes.map(item => item.supplier))];
        const supplierColorMap = {};
        const colorPalette = getColorPalette(uniqueSuppliers.length);
        uniqueSuppliers.forEach((supplier, index) => {
            supplierColorMap[supplier] = colorPalette[index] + "40";
        });
        
        ctx.save();
        
        supplierTimeframes.forEach(timeframe => {
            const startX = scales.x.getPixelForValue(new Date(timeframe.start_timestamp));
            const endX = scales.x.getPixelForValue(new Date(timeframe.end_timestamp));
            
            // Only draw if the timeframe is within the visible chart area
            if (endX >= chartArea.left && startX <= chartArea.right) {
                const clampedStartX = Math.max(startX, chartArea.left);
                const clampedEndX = Math.min(endX, chartArea.right);
                
                ctx.fillStyle = supplierColorMap[timeframe.supplier];
                ctx.fillRect(
                    clampedStartX,
                    chartArea.top,
                    clampedEndX - clampedStartX,
                    chartArea.bottom - chartArea.top
                );
            }
        });
        
        ctx.restore();
    }
};

// Helper function to find active supplier at a given timestamp
export const getSupplierAtTimestamp = (timestamp, supplierTimeframes) => {
    const date = new Date(timestamp);
    return supplierTimeframes.find(timeframe => {
        const start = new Date(timeframe.start_timestamp);
        const end = new Date(timeframe.end_timestamp);
        return date >= start && date <= end;
    });
};

// Helper function to find all suppliers in a time range
export const getSuppliersInTimeRange = (startTimestamp, endTimestamp, supplierTimeframes) => {
    const start = new Date(startTimestamp);
    const end = new Date(endTimestamp);
    return supplierTimeframes.filter(timeframe => {
        const supplierStart = new Date(timeframe.start_timestamp);
        const supplierEnd = new Date(timeframe.end_timestamp);
        // Check if there's any overlap between the time ranges
        return (start <= supplierEnd && end >= supplierStart);
    });
};

// Get supplier footer configuration for tooltips
export const getSupplierFooterConfig = (supplierTimeframes, timeIntervalMinutes) => ({
    footerColor: '#888',
    footerFont: {
        size: 12,
        style: 'normal',
        weight: 'normal'
    },
    footerAlign: 'left',
    footerMarginTop: 16,
    footer: (tooltipItems) => {
        const timestampStart = tooltipItems[0].parsed.x;
        const timestampEnd = timestampStart + timeIntervalMinutes * 60 * 1000;
        const activeSuppliers = getSuppliersInTimeRange(timestampStart, timestampEnd, supplierTimeframes);
        
        if (activeSuppliers.length === 0) {
            return [];
        }

        // Get unique supplier names
        const uniqueSuppliers = [...new Set(activeSuppliers.map(s => s.supplier))];
        if (uniqueSuppliers.length === 1) {
            return uniqueSuppliers[0];
        } else if (uniqueSuppliers.length <= 3) {
            return uniqueSuppliers
        } else {
            const remaining = uniqueSuppliers.length - 2;
            return [...uniqueSuppliers.slice(0, 2), `+ ${remaining} weitere...`];
        }
    }
});

// React component for supplier legend
export const SupplierLegend = ({ supplierTimeframes, maxNumberOfSuppliers = 10 }) => {
    if (!supplierTimeframes || supplierTimeframes.length === 0) return null;

    // Create supplier color mapping
    const uniqueSuppliers = [...new Set(supplierTimeframes.map(item => item.supplier))];
    const supplierColorMap = {};
    const colorPalette = getColorPalette(uniqueSuppliers.length);
    uniqueSuppliers.forEach((supplier, index) => {
        supplierColorMap[supplier] = colorPalette[index];
    });

    const visibleSuppliers = uniqueSuppliers.slice(0, maxNumberOfSuppliers);
    const hiddenSuppliers = uniqueSuppliers.slice(maxNumberOfSuppliers);

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            rowGap: '4px',
            marginTop: '10px',
            fontSize: '12px',
            color: '#666',
            flexWrap: 'wrap',
            width: '100%',
            position: 'relative',
            height: 'fit-content',
        }}>
            {visibleSuppliers.map((supplier) => (
                <div key={supplier} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexShrink: 0, // Prevents items from shrinking
                    maxWidth: '150px' // Maximum width for each supplier item
                }}>
                    <div style={{
                        flexShrink: 0, // Prevents the dot from shrinking
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: supplierColorMap[supplier]
                    }} />
                    <span style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>{supplier}</span>
                </div>
            ))}
            {hiddenSuppliers.length > 0 && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexShrink: 0,
                    color: '#666'
                }}>
                    <span>+{hiddenSuppliers.length} weitere...</span>
                </div>
            )}
        </div>
    );
};

// Get supplier plugin configuration
export const getSupplierPluginConfig = (supplierTimeframes) => ({
    supplierBackground: {
        supplierTimeframes: supplierTimeframes
    }
}); 